import { useState } from 'react';
import { Check, ChevronRight, Swords, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Loader } from '@/components/common/Loader';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import {
  useAcceptChallengeMutation,
  useCancelChallengeMutation,
  useDeclineChallengeMutation,
  useListChallengesQuery,
} from '@/services/qduelsApi';
import { duelFormatLabelKey, duelGameModeLabelKey } from '@/types/qduels';
import type { ChallengeDTO } from '@/types/qduels';

/**
 * Le SFIDE di QDuels (il corpo del tab «Sfide» di Amici, lato QDuels), estratte da `routes/Friends.tsx` (v3 fase 2, PR 7-bis) SENZA cambiare niente: stesso codice, riga per riga, stesso
 * DOM (lo prova `tests/sfide-pannello-caratterizzazione.test.tsx`, scritto su main prima dello spostamento e con lo stesso snapshot dopo). Le usa Amici e, nella v3, la chiave SFIDE di
 * Clash Royale (la Mode Rail).
 */

/** Challenge lifecycle states that still need attention / are live. */
const ACTIVE_STATUSES = new Set<ChallengeDTO['status']>(['pending', 'accepted', 'in_progress']);

/**
 * The CR game tag to open in DuelResult for a finished challenge: the decisive
 * (last) match in `matchTags`, falling back to `currentMatchTag`. Returns null
 * when the challenge carries no match tag (declined/expired/cancelled before any
 * match was played) — the row then stays inert.
 */
function challengeResultTag(c: ChallengeDTO): string | null {
  const last = c.matchTags?.[c.matchTags.length - 1];
  return last ?? c.currentMatchTag ?? null;
}

/**
 * "Sfide" tab — in-app duel challenges. Incoming (role=defender) can be
 * accepted/declined; outgoing (role=challenger) can be cancelled while pending.
 * On accept, qduels-api creates the hosted Supercell match and BOTH players get
 * the in-game invite — so the flow lives entirely in-app, no QBot DM required.
 * The incoming list is polled by the parent; outgoing is polled here.
 */
export function ChallengesPanel({
  incoming,
  incomingLoading,
  incomingError,
}: {
  incoming: ChallengeDTO[];
  incomingLoading: boolean;
  incomingError: boolean;
}) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const outgoingQuery = useListChallengesQuery(
    { role: 'challenger' },
    { pollingInterval: 6_000, refetchOnFocus: true },
  );
  const outgoing = outgoingQuery.data ?? [];

  const [accept, acceptState] = useAcceptChallengeMutation();
  const [decline, declineState] = useDeclineChallengeMutation();
  const [cancel, cancelState] = useCancelChallengeMutation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const busy = acceptState.isLoading || declineState.isLoading || cancelState.isLoading;

  const run = async (
    id: string,
    fn: (id: string) => { unwrap: () => Promise<unknown> },
    impact: 'success' | 'error' = 'success',
  ) => {
    if (busy) return;
    setBusyId(id);
    setActionError(null);
    haptics.impact('medium');
    try {
      await fn(id).unwrap();
      haptics.notification(impact);
    } catch {
      haptics.notification('error');
      setActionError(
        t('social.duel_action_error', {
          defaultValue: 'Action failed — the challenge may have expired. Try again.',
        }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const openResult = (c: ChallengeDTO) => {
    const tag = challengeResultTag(c);
    if (!tag) return;
    haptics.impact('light');
    navigate(
      `/duels/result/${encodeURIComponent(tag)}?challengeId=${encodeURIComponent(c.challengeId)}`,
    );
  };

  const activeIncoming = incoming.filter((c) => ACTIVE_STATUSES.has(c.status));
  const activeOutgoing = outgoing.filter((c) => ACTIVE_STATUSES.has(c.status));
  const recent = [...incoming, ...outgoing]
    .filter((c) => !ACTIVE_STATUSES.has(c.status))
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, 5);

  if (incomingLoading && !incoming.length) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (incomingError) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        {t('friendsPanel.loadFailed')}
      </div>
    );
  }

  const nothing = !activeIncoming.length && !activeOutgoing.length && !recent.length;
  if (nothing) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {t('social.empty_duels', {
          defaultValue: 'No challenges. Tap "Sfida" on a friend to start a duel.',
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {actionError && (
        <div className="rounded-xl bg-danger/15 p-3 text-center text-xs text-danger">
          {actionError}
        </div>
      )}

      {activeIncoming.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.incoming', { defaultValue: 'Incoming' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {activeIncoming.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="incoming"
                busy={busyId === c.challengeId}
                onAccept={() => run(c.challengeId, accept)}
                onDecline={() => run(c.challengeId, decline)}
              />
            ))}
          </ul>
        </section>
      )}

      {activeOutgoing.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.outgoing', { defaultValue: 'Sent' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {activeOutgoing.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="outgoing"
                busy={busyId === c.challengeId}
                onCancel={() => run(c.challengeId, cancel)}
              />
            ))}
          </ul>
        </section>
      )}

      {recent.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.duel_recent', { defaultValue: 'Recent' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {recent.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="recent"
                busy={false}
                onOpenResult={
                  c.status === 'completed' && challengeResultTag(c)
                    ? () => openResult(c)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** A single duel challenge row, action set driven by `role` + status. */
function ChallengeCard({
  challenge: c,
  role,
  busy,
  onAccept,
  onDecline,
  onCancel,
  onOpenResult,
}: {
  challenge: ChallengeDTO;
  role: 'incoming' | 'outgoing' | 'recent';
  busy: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
  /** When set (completed challenge), the whole row opens DuelResult on tap. */
  onOpenResult?: () => void;
}) {
  const { t } = useTranslation('general');

  // The opponent is whichever side I'm NOT. For 'recent' rows we can't be sure
  // which side the viewer was without their sub, so we show the challenger.
  const meIsDefender = role === 'incoming';
  const opp = meIsDefender ? c.challenger : c.defender;
  const oppName =
    opp.displayName?.trim() ||
    opp.playerTag ||
    t('social.duel_unknown_player', { defaultValue: 'Player' });
  const myWins = meIsDefender ? (c.winsDefender ?? 0) : (c.winsChallenger ?? 0);
  const oppWins = meIsDefender ? (c.winsChallenger ?? 0) : (c.winsDefender ?? 0);

  const live = c.status === 'in_progress' || c.status === 'accepted';
  const tappable = Boolean(onOpenResult);

  return (
    <li
      className={cn(
        'rounded-xl bg-bg-secondary p-3',
        tappable && 'cursor-pointer transition-opacity active:opacity-80',
      )}
      {...(tappable ? { role: 'button', tabIndex: 0, onClick: onOpenResult } : {})}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted">
          <Swords className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg">{oppName}</p>
          <p className="truncate text-xs text-fg-muted">
            {t(duelFormatLabelKey(c.format), { defaultValue: c.format.toUpperCase() })}
            {c.gameMode
              ? ` · ${t(duelGameModeLabelKey(c.gameMode), { defaultValue: c.gameMode })}`
              : ''}
            {live ? ` · ${myWins}–${oppWins}` : ''}
          </p>
        </div>

        {role === 'incoming' && c.status === 'pending' && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onDecline}
              aria-label={t('social.duel_decline', { defaultValue: 'Decline' })}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onAccept}
              aria-label={t('social.duel_accept', { defaultValue: 'Accept' })}
              className="flex h-8 items-center gap-1 rounded-full bg-brand px-3 text-xs font-bold text-brand-fg disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {t('social.duel_accept', { defaultValue: 'Accept' })}
            </button>
          </div>
        )}

        {role === 'outgoing' && c.status === 'pending' && (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="shrink-0 rounded-full bg-bg-tertiary px-3 py-1.5 text-xs font-bold text-fg-muted disabled:opacity-50"
          >
            {t('social.duel_cancel', { defaultValue: 'Cancel' })}
          </button>
        )}

        {tappable && <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" />}
      </div>

      {live && (
        <p className="mt-2 rounded-lg bg-brand/10 px-3 py-2 text-xs font-medium text-brand">
          {t('social.duel_open_cr', {
            defaultValue: 'Match on — open Clash Royale and accept the invite to play!',
          })}
        </p>
      )}

      {role === 'recent' && (
        <p className="mt-1 text-xs text-fg-muted">{statusLabel(c.status, t)}</p>
      )}
    </li>
  );
}

function statusLabel(
  status: ChallengeDTO['status'],
  t: (k: string, o: { defaultValue: string }) => string,
): string {
  switch (status) {
    case 'completed':
      return t('social.duel_completed', { defaultValue: 'Completed' });
    case 'declined':
      return t('social.duel_declined', { defaultValue: 'Declined' });
    case 'cancelled':
      return t('social.duel_cancelled', { defaultValue: 'Cancelled' });
    case 'expired':
      return t('social.duel_expired', { defaultValue: 'Expired (not accepted in time)' });
    case 'abandoned':
      return t('social.duel_abandoned', { defaultValue: 'Abandoned' });
    default:
      return status;
  }
}
